import React, { useState, useRef, useEffect } from "react";
import { Camera, X, RotateCcw, Check, Upload, Loader2, AlertCircle } from "lucide-react";

const DocumentScanner = ({ onCapture, onClose, side = "front", documentType = "INE" }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [isScanning, setIsScanning] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [error, setError] = useState(null);
  const [facingMode, setFacingMode] = useState("environment"); // 'user' para frontal, 'environment' para trasera

  useEffect(() => {
    startCamera();
    return () => {
      stopCamera();
    };
  }, [facingMode]);

  const startCamera = async () => {
    try {
      setError(null);
      
      // Verificar si hay dispositivos de medios disponibles
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setError("Tu navegador no soporta el acceso a la cámara. Por favor, usa un navegador más reciente.");
        return;
      }

      const constraints = {
        video: {
          facingMode: facingMode,
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        setIsScanning(true);
      }
    } catch (err) {
      console.error("Error accessing camera:", err);
      if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
        setError("Se denegó el acceso a la cámara. Por favor, permite el acceso en la configuración de tu navegador.");
      } else if (err.name === "NotFoundError" || err.name === "DevicesNotFoundError") {
        setError("No se encontró ninguna cámara disponible en tu dispositivo.");
      } else {
        setError("No se pudo acceder a la cámara. Por favor, verifica los permisos e intenta nuevamente.");
      }
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsScanning(false);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], `${documentType}_${side}_${Date.now()}.jpg`, {
          type: "image/jpeg",
        });
        setCapturedImage(URL.createObjectURL(blob));
        stopCamera();
      }
    }, "image/jpeg", 0.9);
  };

  const retakePhoto = () => {
    setCapturedImage(null);
    startCamera();
  };

  const confirmPhoto = () => {
    if (capturedImage && canvasRef.current) {
      canvasRef.current.toBlob((blob) => {
        if (blob) {
          const file = new File([blob], `${documentType}_${side}_${Date.now()}.jpg`, {
            type: "image/jpeg",
          });
          onCapture(file);
          onClose();
        }
      }, "image/jpeg", 0.9);
    }
  };

  const switchCamera = () => {
    stopCamera();
    setFacingMode(facingMode === "environment" ? "user" : "environment");
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#218e01] to-[#5fc500] p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Camera className="w-6 h-6 text-white" />
            <h3 className="text-lg font-bold text-white">
              Escanear {documentType === "Pasaporte" ? "Pasaporte" : "INE"} - {side === "front" ? "Frente" : "Reverso"}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Scanner Content */}
        <div className="p-6">
          {error ? (
            <div className="text-center py-12">
              <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <p className="text-red-600 font-medium mb-4">{error}</p>
              <button
                onClick={startCamera}
                className="px-6 py-3 bg-gradient-to-r from-[#218e01] to-[#5fc500] text-white rounded-xl font-semibold hover:shadow-lg transition-all"
              >
                Intentar Nuevamente
              </button>
            </div>
          ) : capturedImage ? (
            <div className="space-y-4">
              <div className="relative bg-slate-100 rounded-xl overflow-hidden">
                <img
                  src={capturedImage}
                  alt="Captured document"
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 border-4 border-[#5fc500] rounded-xl pointer-events-none"></div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={retakePhoto}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border-2 border-slate-300 rounded-xl font-semibold text-slate-700 hover:bg-slate-50 transition-all"
                >
                  <RotateCcw className="w-5 h-5" />
                  Volver a Tomar
                </button>
                <button
                  onClick={confirmPhoto}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-[#218e01] to-[#5fc500] text-white rounded-xl font-semibold hover:shadow-lg transition-all"
                >
                  <Check className="w-5 h-5" />
                  Confirmar
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Video Preview */}
              <div className="relative bg-black rounded-xl overflow-hidden aspect-video">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover"
                />
                {isScanning && (
                  <>
                    {/* Overlay Guide */}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="border-4 border-[#5fc500] rounded-xl w-[85%] h-[60%] shadow-[0_0_0_9999px_rgba(0,0,0,0.5)]"></div>
                    </div>
                    {/* Instructions */}
                    <div className="absolute bottom-4 left-0 right-0 text-center pointer-events-none">
                      <p className="text-white font-semibold drop-shadow-lg bg-black/50 px-4 py-2 rounded-lg inline-block">
                        Coloca el documento dentro del marco
                      </p>
                    </div>
                  </>
                )}
              </div>

              {/* Controls */}
              <div className="flex gap-3">
                <button
                  onClick={switchCamera}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border-2 border-slate-300 rounded-xl font-semibold text-slate-700 hover:bg-slate-50 transition-all"
                >
                  <RotateCcw className="w-5 h-5" />
                  Cambiar Cámara
                </button>
                <button
                  onClick={capturePhoto}
                  disabled={!isScanning}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-[#218e01] to-[#5fc500] text-white rounded-xl font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Camera className="w-5 h-5" />
                  Capturar
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Hidden Canvas */}
        <canvas ref={canvasRef} className="hidden" />
      </div>
    </div>
  );
};

export default DocumentScanner;

